import { Button, ConfigProvider, Form, message, Modal } from "antd";
import { FC, useEffect, useState } from "react";

import styles from "./index.less";

import { ReactComponent as AutoRenewIcon } from "../../assets/svg/auto-renew.svg";

import { PricingPlanVersionEnum, PricingPlanVersionTextMap } from "../../types";
import { ResponseCode } from "../../constants";
import { postAutoRenewPlanService } from "../../service";

import RenewSwitcher from "./components/RenewSwitcher";
import VersionPicker from "./components/VersionPicker";

import { PrimaryTheme } from "@packages/theme";

interface PlanAutoRenewProps {
  currentVersion: PricingPlanVersionEnum;
  nextPeriodAt: string;
  initialValue?: {
    status: boolean;
    plan: PricingPlanVersionEnum;
  };
  onSuccess: () => void;
}

/**
 * 自动续费组件
 * @returns
 */
const PlanAutoRenew: FC<PlanAutoRenewProps> = ({
  currentVersion,
  nextPeriodAt,
  initialValue,
  onSuccess,
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const [form] = Form.useForm();
  const autoRenew = Form.useWatch("status", form);

  useEffect(() => {
    if (open) {
      if (initialValue) {
        form.setFieldsValue(initialValue);
        return;
      }

      // 无默认值
      form.setFieldsValue({
        status: false,
        plan: currentVersion,
      });
    }
  }, [open, initialValue]);

  const handleConfirm = async () => {
    try {
      const values = await form.validateFields();
      const data = values.status
        ? {
            status: 1,
            plan: values.plan,
          }
        : {
            status: 0,
          };

      const rsp = await postAutoRenewPlanService(data);

      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return;
      }

      if (values.status) {
        message.success(
          `已确认下一周期续费版本：${
            PricingPlanVersionTextMap[values.plan as PricingPlanVersionEnum]
          } 版`
        );
      } else {
        message.success("已关闭自动续费");
      }

      form.resetFields();
      setOpen(false);
      onSuccess();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <ConfigProvider theme={PrimaryTheme}>
      <div className={styles["auto-renew"]} onClick={() => setOpen(true)}>
        <AutoRenewIcon />
        自动续费：
        {initialValue?.status
          ? `${PricingPlanVersionTextMap[initialValue.plan]} 版`
          : "未开启"}
      </div>

      <Modal
        open={open}
        width={520}
        title="续费管理"
        maskClosable={false}
        onClose={() => {
          form.resetFields();
          setOpen(false);
        }}
        onCancel={() => {
          form.resetFields();
          setOpen(false);
        }}
        footer={
          <div className={styles["footer"]}>
            <Button
              onClick={() => {
                form.resetFields();
                setOpen(false);
              }}
            >
              取消
            </Button>
            <Button type="primary" onClick={handleConfirm}>
              确认
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="status">
            <RenewSwitcher />
          </Form.Item>

          {autoRenew && (
            <>
              <Form.Item
                name="plan"
                label={
                  <div className={styles["form-label"]}>
                    计划下一周期续费的版本
                  </div>
                }
              >
                <VersionPicker currentVersion={currentVersion} />
              </Form.Item>

              <div className={styles["next-renew-date"]}>
                <div className={styles["label"]}>下次续费日期：</div>
                <div className={styles["date"]}>{nextPeriodAt}</div>
              </div>
            </>
          )}
        </Form>
      </Modal>
    </ConfigProvider>
  );
};

export default PlanAutoRenew;
